import React, { useEffect, useState } from "react";
import axios from "axios";
import { Factory, History, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { CollapsibleSection, fmtTL } from "./CollapsibleSection";

const PER_PAGE = 30;
const FILTERS = [
  ["all", "Tümü"], ["sale", "Satış"], ["purchase", "Mal Alış"], ["collection", "Tahsilat"],
  ["payment", "Ödeme"], ["temlik", "Temlik"], ["expense", "Gider"], ["activity", "Personel"],
];
const KIND_STYLES = {
  sale: "bg-blue-100 text-blue-800", purchase: "bg-orange-100 text-orange-800",
  collection: "bg-green-100 text-green-800", payment: "bg-red-100 text-red-800",
  temlik: "bg-indigo-100 text-indigo-800", expense: "bg-rose-100 text-rose-800",
  activity: "bg-gray-100 text-gray-800", login: "bg-slate-100 text-slate-600",
};
const LABEL_STYLES = { "Üretim": "bg-amber-100 text-amber-800", "Montaj": "bg-teal-100 text-teal-800" };

export function OperationRow({ item }) {
  return (
  <div className="flex items-start gap-3 py-2.5 border-b last:border-b-0" data-testid={`recent-op-${item.kind}-${item.id}`}>
    <span className={`shrink-0 w-28 text-center px-2 py-0.5 rounded text-xs font-medium truncate ${LABEL_STYLES[item.label] || KIND_STYLES[item.kind] || KIND_STYLES.activity}`} title={item.label}>{item.label}</span>
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium text-gray-900 break-words">{item.title || "-"}</p>
      {item.description && <p className="text-xs text-gray-600 break-words">{item.description}</p>}
      <p className="text-xs text-gray-400">
        {item.date ? new Date(item.date).toLocaleString("tr-TR") : ""}
        {item.user && item.user !== "-" && item.user !== item.title && ` • ${item.user}`}
      </p>
    </div>
    {item.amount != null && <span className="text-sm font-semibold whitespace-nowrap">{fmtTL(item.amount)}</span>}
  </div>
  );
}

const useOperations = (api, sessionToken, kind, includeLogins) => {
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async (skip = 0) => {
    setLoading(true);
    try {
      const res = await axios.get(`${api}/dashboard/recent-operations`, {
        params: { session_token: sessionToken, kind, include_logins: includeLogins, limit: PER_PAGE, skip },
      });
      setItems((prev) => (skip === 0 ? res.data.items : [...prev, ...res.data.items]));
      setHasMore(res.data.has_more);
    } catch (e) {
      console.error("Son işlemler alınamadı:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (sessionToken) load(0); }, [sessionToken, kind, includeLogins]);
  return { items, hasMore, loading, loadMore: () => load(items.length) };
};

const OperationList = ({ feed, testId }) => (
  <>
    <div data-testid={testId}>
      {feed.items.map((item) => <OperationRow key={`${item.kind}-${item.id}`} item={item} />)}
      {!feed.loading && feed.items.length === 0 && <p className="text-center text-sm text-gray-500 py-6" data-testid={`${testId}-empty`}>İşlem bulunamadı</p>}
    </div>
    {feed.loading && <div className="flex justify-center py-3"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>}
    {feed.hasMore && !feed.loading && (
      <div className="flex justify-center mt-3">
        <Button variant="outline" size="sm" onClick={feed.loadMore} data-testid={`${testId}-load-more`}>Daha fazla göster</Button>
      </div>
    )}
  </>
);

export const RecentOperations = ({ api, sessionToken, isAdmin }) => {
  const [kind, setKind] = useState("all");
  const [includeLogins, setIncludeLogins] = useState(false);
  const feed = useOperations(api, sessionToken, kind, includeLogins);
  const erpFeed = useOperations(api, sessionToken, "erp", false);

  return (
    <CollapsibleSection id="recent" title="Son İşlemler" icon={<History className="h-5 w-5" />} titleClass="text-slate-800">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {FILTERS.map(([key, label]) => (
              <Button key={key} size="sm" variant={kind === key ? "default" : "outline"} onClick={() => setKind(key)} data-testid={`recent-ops-filter-${key}`}>
                {label}
              </Button>
            ))}
            {isAdmin && (
              <label className="flex items-center gap-2 text-sm text-gray-600 ml-auto">
                <Switch checked={includeLogins} onCheckedChange={setIncludeLogins} data-testid="recent-ops-include-logins" />
                Giriş/çıkışları göster
              </label>
            )}
          </div>
          <OperationList feed={feed} testId="recent-ops-list" />
        </div>
        <div className="xl:border-l xl:pl-6" data-testid="erp-ops-panel">
          <h3 className="flex items-center gap-2 font-semibold text-amber-800 mb-3">
            <Factory className="h-5 w-5" /> ERP İşlemleri
            <span className="text-xs font-normal text-gray-500">(Üretim & Montaj ekranları)</span>
          </h3>
          <OperationList feed={erpFeed} testId="erp-ops-list" />
        </div>
      </div>
    </CollapsibleSection>
  );
};
